import React, { useEffect, useState } from "react";
import MaterialTable from "material-table";
import { Box, Button, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
    GetAtsAgreementMastersHelper,
    AddAtsAgreementMasterHelper,
    UpdateAtsAgreementMasterHelper,
    DeleteAtsAgreementMasterHelper
} from "../../../ATS/helper/AtsAgreementHelper";

const AgreementMasterTable = ({ masterType, title, hasValueField, valueLabel }) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadData = () => {
        setIsLoading(true);
        GetAtsAgreementMastersHelper(masterType).then((res) => {
            if (res && !res.hasError) {
                // Map database value fields if needed
                const items = (res.data?.data || []).map(item => ({
                    ...item,
                    value: item.value || item.value_pct || item.value_months || ""
                }));
                setData(items);
            }
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    };

    useEffect(() => {
        loadData();
    }, [masterType]);

    const isImageMaster = masterType === "header_image" || masterType === "footer_image";

    const columns = [
        {
            title: isImageMaster ? "Image Name / Label" : "Name",
            field: "name",
            cellStyle: { padding: "0px 10px" },
            validate: rowData => rowData?.name ? '' : 'Name cannot be empty',
            editComponent: props => (
                <TextField
                    required
                    variant="standard"
                    value={props.value || ""}
                    onChange={e => props.onChange(e.target.value)}
                    error={props.error}
                    helperText={props.helperText}
                    fullWidth
                />
            )
        }
    ];

    if (hasValueField || isImageMaster) {
        columns.push({
            title: isImageMaster ? "Image File" : (valueLabel || "Value"),
            field: "value",
            cellStyle: { padding: "0px 10px" },
            validate: rowData => rowData?.value ? '' : 'File/Value is required',
            render: rowData => {
                if (isImageMaster) {
                    return (
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                            {rowData.value}
                        </Typography>
                    );
                }
                return rowData.value;
            },
            editComponent: props => {
                if (isImageMaster) {
                    const handleFileSelect = (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            // Update both name (label) and base64 value
                            props.onChange(reader.result);
                            if (!props.rowData.name) {
                                props.onRowDataChange({
                                    ...props.rowData,
                                    name: file.name,
                                    value: reader.result
                                });
                            }
                        };
                        reader.readAsDataURL(file);
                    };

                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                size="small"
                            >
                                Upload
                                <input type="file" hidden accept="image/*" onChange={handleFileSelect} />
                            </Button>
                            {props.value && (
                                <Typography variant="caption" sx={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {props.value.startsWith("data:") ? "New File Chosen" : props.value}
                                </Typography>
                            )}
                        </Box>
                    );
                }

                return (
                    <TextField
                        required
                        variant="standard"
                        value={props.value || ""}
                        onChange={e => props.onChange(e.target.value)}
                        error={props.error}
                        helperText={props.helperText}
                        fullWidth
                    />
                );
            }
        });
    }

    return (
        <Box>
            <MaterialTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                icons={{
                    Add: () => <Button component="span" variant="contained" startIcon={<AddIcon />} className="mpl-primary-btn">Add</Button>,
                    Edit: () => <EditIcon />,
                    Delete: () => <DeleteOutlineIcon color='error' />
                }}
                editable={{
                    onRowAdd: (newRow) =>
                        new Promise((resolve) => {
                            AddAtsAgreementMasterHelper(masterType, newRow.name, newRow.value).then(() => {
                                loadData();
                                resolve();
                            });
                        }),
                    onRowUpdate: (newRow, oldRow) =>
                        new Promise((resolve) => {
                            UpdateAtsAgreementMasterHelper(masterType, newRow.id, newRow.name, newRow.value).then(() => {
                                loadData();
                                resolve();
                            });
                        }),
                    onRowDelete: (selectedRow) =>
                        new Promise((resolve) => {
                            DeleteAtsAgreementMasterHelper(masterType, selectedRow.id).then(() => {
                                loadData();
                                resolve();
                            });
                        }),
                }}
                options={{
                    maxBodyHeight: "25vh",
                    minBodyHeight: "20vh",
                    rowStyle: { fontSize: "13px" },
                    headerStyle: { padding: "0px 10px", fontWeight: "bold" },
                    sorting: true,
                    search: false,
                    filtering: false,
                    paging: true,
                    pageSize: 10,
                    actionsColumnIndex: -1,
                    addRowPosition: "first",
                    showTitle: false,
                }}
            />
        </Box>
    );
};

export default AgreementMasterTable;
